import { useState, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import '../../css/home/Banner.css';
import { useLang } from '../../i18n';
import { useGeo } from '../../geo';

export default function Banner() {
  const { t } = useLang();
  const geo = useGeo();
  const navigate = useNavigate();
  const { depositBonusCap, symbol } = geo.prices;

  // Offre 1er dépôt : non connecté → inscription ; connecté → page de dépôt.
  const claimOffer = () => {
    const user = (() => { try { return JSON.parse(localStorage.getItem('user')); } catch { return null; } })();
    const loggedIn = user?.id && localStorage.getItem('token');
    navigate(loggedIn ? '/deposit' : '/auth?tab=signup');
  };

  const slides = [
    {
      bg: '/img/banner-3.png',
      title: t('home.bannerTitle'),
      sub: t('home.bannerSub', { amount: `${depositBonusCap} ${symbol}` }),
      cta: t('home.bannerCta'),
      onCta: claimOffer,
    },
    {
      bg: '/img/banner-2.png',
      title: t('home.accaBoostTitle'),
      sub: t('home.accaBoostSub'),
      // bouton « Profiter » retiré
    },
    {
      bg: '/img/banner-1.png',
      title: t('home.telegramTitle'),
      sub: t('home.telegramSub'),
      cta: t('home.telegramCta'),
      onCta: () => window.open('https://t.me/officialsupport_bet', '_blank', 'noopener,noreferrer'),
    },
  ];

  const [idx, setIdx] = useState(0);
  const [offset, setOffset] = useState(0);
  const [dragging, setDragging] = useState(false);
  const startX = useRef(null);

  const prev = () => setIdx((i) => (i - 1 + slides.length) % slides.length);
  const next = () => setIdx((i) => (i + 1) % slides.length);

  const handleDragStart = (x) => {
    startX.current = x;
    setDragging(true);
    setOffset(0);
  };

  const handleDragMove = (x) => {
    if (startX.current === null) return;
    setOffset(x - startX.current);
  };

  const handleDragEnd = (x) => {
    if (startX.current === null) return;
    const delta = x - startX.current;
    if (delta < -50) next();
    else if (delta > 50) prev();
    setOffset(0);
    setDragging(false);
    startX.current = null;
  };

  const onTouchStart = (e) => handleDragStart(e.touches[0].clientX);
  const onTouchMove  = (e) => handleDragMove(e.touches[0].clientX);
  const onTouchEnd   = (e) => handleDragEnd(e.changedTouches[0].clientX);

  const onMouseDown  = (e) => { e.preventDefault(); handleDragStart(e.clientX); };
  const onMouseMove  = (e) => { if (dragging) handleDragMove(e.clientX); };
  const onMouseUp    = (e) => { if (dragging) handleDragEnd(e.clientX); };
  const onMouseLeave = ()  => { if (dragging) { setOffset(0); setDragging(false); startX.current = null; } };

  const trackStyle = {
    transform: `translateX(calc(${-idx * 100}% + ${offset}px))`,
    transition: dragging ? 'none' : 'transform 0.35s cubic-bezier(.25,.46,.45,.94)',
  };

  return (
    <section
      className="banner"
      onTouchStart={onTouchStart}
      onTouchMove={onTouchMove}
      onTouchEnd={onTouchEnd}
      onMouseDown={onMouseDown}
      onMouseMove={onMouseMove}
      onMouseUp={onMouseUp}
      onMouseLeave={onMouseLeave}
      style={{ cursor: dragging ? 'grabbing' : 'grab', userSelect: 'none' }}
    >
      <div className="banner-track" style={trackStyle}>
        {slides.map((slide, i) => (
          <div key={i} className="banner-slide">
            <div className="banner-bg" style={{ backgroundImage: `url(${slide.bg})` }} />
            <div className="banner-content">
              <div className="banner-title">{slide.title}</div>
              <div className="banner-sub">{slide.sub}</div>
              {slide.cta && (
                <button
                  className="banner-cta"
                  onClick={(e) => { e.stopPropagation(); slide.onCta?.(); }}
                >{slide.cta}</button>
              )}
              <div className="banner-arrows">
                <span className="banner-arrow" onClick={prev}>
                  <svg width="6" height="9" viewBox="0 0 6 9">
                    <path d="M5 1L1.5 4.5 5 8" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
                <span className="banner-arrow" onClick={next}>
                  <svg width="6" height="9" viewBox="0 0 6 9">
                    <path d="M1 1l3.5 3.5L1 8" stroke="#fff" strokeWidth="1.2" fill="none" strokeLinecap="round" strokeLinejoin="round" />
                  </svg>
                </span>
              </div>
            </div>
          </div>
        ))}
      </div>

      <div className="banner-dots">
        {slides.map((_, i) => (
          <span
            key={i}
            className={'banner-dot' + (i === idx ? ' active' : '')}
            onClick={() => setIdx(i)}
          />
        ))}
      </div>
    </section>
  );
}
